import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useInboxPanel } from "@/hooks/useInboxPanel";

export const RO_DETAILS_TAB = "roDetails";
export const SEARCH_RESULTS_TAB = "searchResults";

export interface RoPanelParams {
  selectedCardId: string | null;
  activeTab: string | null;
  /** Whether the board should paint its selection ring right now. */
  isSelectionVisible: boolean;
  selectCard: (cardId: string) => void;
  clearSelectedCard: () => void;
  setActiveTab: (tab: string, options?: { persist?: boolean }) => void;
  submitSearch: (query: string) => void;
  clearSearchResults: () => void;
}

export const useRoPanelParams = (): RoPanelParams => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    isOpen,
    openPanel,
    rememberedTab,
    rememberedCardId,
    rememberTab,
    rememberCardId,
    setHasSearchResults,
  } = useInboxPanel();

  const selectCard = useCallback(
    (cardId: string) => {
      rememberCardId(cardId);
      rememberTab(RO_DETAILS_TAB);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("ro", cardId);
          // Always jump to RO Details when a card is clicked, regardless of
          // which side-panel tab is currently open (or if it's closed).
          next.set("tab", RO_DETAILS_TAB);
          return next;
        },
        { replace: true }
      );
      openPanel();
    },
    [setSearchParams, openPanel, rememberCardId, rememberTab]
  );

  const clearSelectedCard = useCallback(() => {
    rememberCardId(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("ro");
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams, rememberCardId]);

  const setActiveTab = useCallback(
    (tab: string, options?: { persist?: boolean }) => {
      rememberTab(tab, options);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", tab);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, rememberTab]
  );

  const submitSearch = useCallback(
    (query: string) => {
      if (!query.trim()) return;

      setHasSearchResults(true);
      rememberTab(SEARCH_RESULTS_TAB);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", SEARCH_RESULTS_TAB);
          return next;
        },
        { replace: true }
      );
      openPanel();
    },
    [setSearchParams, openPanel, rememberTab, setHasSearchResults]
  );

  // The URL wins when it carries a value, so a shared deep link beats
  // remembered state. The provider covers the navigations that drop the query
  // string entirely -- notably the grid/list links in ViewToggle.
  const selectedCardId = searchParams.get("ro") ?? rememberedCardId;
  const activeTab = searchParams.get("tab") ?? rememberedTab;

  const clearSearchResults = useCallback(() => {
    setHasSearchResults(false);
    if (activeTab === SEARCH_RESULTS_TAB) {
      setActiveTab(RO_DETAILS_TAB);
    }
  }, [activeTab, setActiveTab, setHasSearchResults]);

  return {
    selectedCardId,
    activeTab,
    // The selection itself survives closing the panel; only the ring is tied
    // to RO Details actually being on screen.
    isSelectionVisible: isOpen && activeTab === RO_DETAILS_TAB,
    selectCard,
    clearSelectedCard,
    setActiveTab,
    submitSearch,
    clearSearchResults,
  };
};
